import {ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit} from '@angular/core';
import {
  CodebookService,
  DocumentSearchService,
  EntityInfoComponent,
  isDocumentObject,
  isFileObject,
  isStorageUnitObject,
  WITHOUT_REF_NUMBER
} from '|shared';
import {
  ApiFileService,
  ApiRegistryOfficeTransferService,
  DocumentDto,
  FileDto,
  RegistryOfficeTransferCreateDto,
  RegistryOfficeTransferPrepareDto,
  StorageUnitDto
} from '|api/document';
import {EntityType, EsslErrorSeverity} from '|api/commons';
import {
  ButtonComponent,
  FormButtonsComponent,
  IconComponent,
  InaccessibleDirective,
  InterpolationContext,
  LoadingIndicatorService
} from '@icz/angular-essentials';
import {
  FormAutocompleteComponent,
  IczFormContainerDirective,
  IczFormControl,
  IczFormGroup,
  IczOption,
  IczValidators
} from '@icz/angular-form-elements';
import {injectModalData, injectModalRef} from '@icz/angular-modal';
import {forkJoin, map, Observable, of} from 'rxjs';
import {ReactiveFormsModule} from '@angular/forms';
import {
  CheckUnsavedFormDialogService,
  IczFormSubmitDirective,
  IFormGroupCheckable, isValidNow,
  MarkdownTemplateOutletComponent, namedDtosToOptions
} from '@icz/angular-application-core';
import {AsyncPipe} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {switchMap} from 'rxjs/operators';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {FilterOperator, FilterTreeOperator} from '@icz/angular-table';
import {SearchRecordSourceDocumentFileDto} from '|api/elastic';
import {z} from 'zod';

export type RegistryOfficeTransferrableObject = FileDto | DocumentDto | StorageUnitDto;

const INCOMPLETE_HANDOVER_CODE = 'be.error.caseFile.sectionIncompleteOnRegistryOfficeHandover';

interface TransferPrepareErrorItem {
  transferrableObject: RegistryOfficeTransferrableObject;
  severity: EsslErrorSeverity;
  errorCode: string;
  errorContext: InterpolationContext;
  relatedFiles?: FileDto[];
}

@Component({
  selector: 'icz-transfer-to-registry-office-dialog',
  templateUrl: './transfer-to-registry-office-dialog.component.html',
  styleUrls: ['./transfer-to-registry-office-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    CheckUnsavedFormDialogService
  ],
  imports: [ReactiveFormsModule, IczFormContainerDirective, InaccessibleDirective, FormAutocompleteComponent, FormButtonsComponent, ButtonComponent, IczFormSubmitDirective, AsyncPipe, EntityInfoComponent, IconComponent, TranslatePipe, MarkdownTemplateOutletComponent]
})
export class TransferToRegistryOfficeDialogComponent implements OnInit, IFormGroupCheckable {

  protected loadingService = inject(LoadingIndicatorService);
  protected modalRef = injectModalRef<Nullable<boolean>>();
  protected checkUnsavedService = inject(CheckUnsavedFormDialogService);
  protected selection = injectModalData<RegistryOfficeTransferrableObject[]>();
  private apiRegistryOfficeTransferService = inject(ApiRegistryOfficeTransferService);
  private documentSearchService = inject(DocumentSearchService);
  private translateService = inject(TranslateService);
  private codebookService = inject(CodebookService);
  private apiFileService = inject(ApiFileService);
  private destroyRef = inject(DestroyRef);

  form = new IczFormGroup({
    registryOfficeId: new IczFormControl<Nullable<number>>(null, [IczValidators.required()]),
  });

  formGroupsToCheck!: string[];
  registryOfficeOptions: IczOption[] = [];

  transferPrepareErrors: Nullable<TransferPrepareErrorItem[]>;

  errorContextSchema = z.object({
    caseFileSectionName: z.optional(z.string()),
    caseFileName: z.optional(z.string()),
  });

  get hasTransferPrepareFailures() {
    return (this.transferPrepareErrors ?? []).filter(e => e.severity === EsslErrorSeverity.ERROR).length > 0;
  }

  ngOnInit(): void {
    this.checkUnsavedService.addUnsavedFormCheck(this, ['form']);

    this.loadingService.doLoading(
      this.codebookService.transferableRegistryOfficesForCurrentFunctionalPosition(),
      this
    ).pipe(
      map(registryOffices => registryOffices.filter(isValidNow)),
      namedDtosToOptions
    ).subscribe(offices => {
      this.registryOfficeOptions = offices;
    });

    this.form.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(_ => {
      if (this.form.valid) {
        this.checkTransferValidity();
      }
    });
  }

  checkTransferValidity() {
    this.loadingService.doLoading(
      this.apiRegistryOfficeTransferService.registryOfficeTransferTransferPrepare({
        body: this.getTransferCommandDto(),
      }).pipe(
        map(prepareResult => prepareResult.filter(res => res.errorCodeList?.length)),
        switchMap(prepareResultWithErrors => {
          const fileIds = prepareResultWithErrors.flatMap(pr => {
            return (pr.relatedEntityList ?? [])
              .filter(relatedEntity => relatedEntity.entityType === EntityType.FILE)
              .map(relatedEntity => relatedEntity.entityId);
          });
          const transferEntities$ = this.documentSearchService.globalSearch(
            {
              page: 0,
              size: 2000,
              complexFilter: {
                operator: FilterTreeOperator.OR,
                values: prepareResultWithErrors.map(pr => ({
                  operator: FilterTreeOperator.AND,
                  values: [
                    {
                      fieldName: 'entityType',
                      operator: FilterOperator.equals,
                      value: pr.entityType,
                    },
                    {
                      fieldName: 'id',
                      operator: FilterOperator.equals,
                      value: String(pr.entityId),
                    }
                  ]
                }))
              }
            },
            false
          ).pipe(
            map(page => page.content ?? []),
          );

          let files$: Observable<FileDto[]> = of([]);

          if (fileIds.length) {
            files$ = this.apiFileService.fileSearch({
              page: 0,
              size: 2000,
              urlPredicates: {
                id: `in(${fileIds})`,
              }
            }).pipe(
              map(page => page.content ?? []),
            );
          }

          return forkJoin([
            of(prepareResultWithErrors),
            transferEntities$,
            files$,
          ]);
        })
      ),
      this
    ).subscribe(([prepareResultWithErrors, transferEntities, files]) => {
      this.transferPrepareErrors = prepareResultWithErrors.flatMap(pr => {
        const transferrableObject = transferEntities.find(e => e.id === pr.entityId && e.entityType === pr.entityType)!;

        return pr.errorCodeList!.map(errorCode => {
          return {
            errorCode,
            errorContext: {
              // Sections have names identical to their associated parts
              caseFileSectionName: (transferrableObject as unknown as SearchRecordSourceDocumentFileDto).caseFile?.partName,
              caseFileName: (transferrableObject as unknown as SearchRecordSourceDocumentFileDto).caseFile?.name,
            },
            transferrableObject,
            severity: errorCode === INCOMPLETE_HANDOVER_CODE ? EsslErrorSeverity.WARN : EsslErrorSeverity.ERROR,
            relatedFiles: files.filter(f => pr.relatedEntityList?.some(re => re.entityId === f.id && re.entityType === EntityType.FILE)),
          };
        });
      });
    });
  }

  getEsslSeverityIcon(severity: EsslErrorSeverity): string {
    switch (severity) {
      case EsslErrorSeverity.ERROR:
        return 'error';
      case EsslErrorSeverity.WARN:
        return 'warning';
      case EsslErrorSeverity.INFO:
        return 'info';
    }
  }

  getEsslSeverityIconClass(severity: EsslErrorSeverity): string {
    return severity.toLowerCase();
  }

  submit() {
    this.loadingService.doLoading(
      this.apiRegistryOfficeTransferService.registryOfficeTransferTransferCreate({
        body: this.getTransferCommandDto(),
      }),
      this
    ).subscribe(_ => {
      this.form.markAsPristine();
      this.modalRef.close(true);
    });
  }

  cancel() {
    this.modalRef.close(null);
  }

  private getTransferCommandDto(): RegistryOfficeTransferCreateDto | RegistryOfficeTransferPrepareDto {
    return {
      registryOfficeId: this.form.get('registryOfficeId')!.value!,
      transferEntities: this.selection.map(s => ({
        entityId: s.id!,
        entityType: isDocumentObject(s) ? EntityType.DOCUMENT : s.entityType!
      }))
    };
  }

  getTransferrableObjectTitle(transferrableObject: RegistryOfficeTransferrableObject) {
    if (isDocumentObject(transferrableObject)) {
      return `${transferrableObject.refNumber ?? this.translateService.instant(WITHOUT_REF_NUMBER)}: ${transferrableObject.subject}`;
    }
    else if (isFileObject(transferrableObject)) {
      return `${transferrableObject.refNumber}: ${transferrableObject.subject}`;
    }
    else if (isStorageUnitObject(transferrableObject)) {
      return `${transferrableObject.storageUnitNumber}: ${transferrableObject.name}`;
    }
    else {
      return '';
    }
  }

}
